import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Radio, ArrowRightLeft, Copy, RotateCcw, Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const MorseCodeConverter: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);

  useSEO({
    title: 'Morse Code Converter - Encode & Decode Morse Code Online',
    description: 'Convert text to Morse code and decode Morse code to text. Includes audio playback feature. Free online Morse code translator.',
    keywords: 'morse code converter, morse code translator, text to morse, morse to text, morse code decoder, morse code encoder',
    canonical: 'https://snap-tools.online/security/morse-code-converter'
  });

  // Morse code mapping
  const morseCodeMap: { [key: string]: string } = {
    'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.',
    'G': '--.', 'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..',
    'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.',
    'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
    'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---',
    '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...',
    '8': '---..', '9': '----.', '.': '.-.-.-', ',': '--..--', '?': '..--..',
    "'": '.----.', '!': '-.-.--', '/': '-..-.', '(': '-.--.', ')': '-.--.-',
    '&': '.-...', ':': '---...', ';': '-.-.-.', '=': '-...-', '+': '.-.-.',
    '-': '-....-', '_': '..--.-', '"': '.-..-.', '$': '...-..-', '@': '.--.-.',
    ' ': '/'
  };

  // Reverse mapping for decoding
  const reverseMorseMap: { [key: string]: string } = {};
  Object.entries(morseCodeMap).forEach(([letter, morse]) => {
    reverseMorseMap[morse] = letter;
  });

  const encodeToMorse = (text: string): string => {
    return text
      .toUpperCase()
      .split('')
      .map(char => morseCodeMap[char] || char)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const decodeFromMorse = (morse: string): string => {
    return morse
      .split(' ')
      .map(code => {
        if (code === '/') return ' ';
        return reverseMorseMap[code] || code;
      })
      .join('')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const handleConvert = () => {
    if (!inputText.trim()) {
      toast.error('Please enter text to convert');
      return;
    }

    try {
      let result = '';
      if (mode === 'encode') {
        result = encodeToMorse(inputText);
      } else {
        result = decodeFromMorse(inputText);
      }
      
      setOutputText(result);
      toast.success(`Text ${mode === 'encode' ? 'encoded' : 'decoded'} successfully`);
    } catch (error) {
      console.error('Conversion error:', error);
      toast.error('Failed to convert text');
    }
  };

  const swapMode = () => {
    setMode(mode === 'encode' ? 'decode' : 'encode');
    setInputText(outputText);
    setOutputText(inputText);
  };

  const copyToClipboard = async (text: string, type: string) => {
    if (!text) {
      toast.error(`No ${type} to copy`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const reset = () => {
    setInputText('');
    setOutputText('');
    setMode('encode');
  };

  const playMorseCode = async () => {
    if (!audioEnabled || !outputText || mode !== 'encode') {
      toast.error('Audio playback is only available for Morse code output');
      return;
    }

    setIsPlaying(true);
    
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const dotDuration = 100; // milliseconds
      const dashDuration = dotDuration * 3;
      const elementGap = dotDuration;
      const letterGap = dotDuration * 3;
      const wordGap = dotDuration * 7;
      const frequency = 600; // Hz

      const playTone = (duration: number) => {
        return new Promise<void>((resolve) => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = frequency;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000);
          
          oscillator.start();
          oscillator.stop(audioContext.currentTime + duration / 1000);
          
          setTimeout(resolve, duration);
        });
      };

      const playGap = (duration: number) => {
        return new Promise<void>((resolve) => {
          setTimeout(resolve, duration);
        });
      };

      const morseElements = outputText.split(' ');
      
      for (let i = 0; i < morseElements.length; i++) {
        const element = morseElements[i];
        
        if (element === '/') {
          await playGap(wordGap);
        } else if (element) {
          for (let j = 0; j < element.length; j++) {
            const char = element[j];
            if (char === '.') {
              await playTone(dotDuration);
            } else if (char === '-') {
              await playTone(dashDuration);
            }
            
            if (j < element.length - 1) {
              await playGap(elementGap);
            }
          }
          
          if (i < morseElements.length - 1 && morseElements[i + 1] !== '/') {
            await playGap(letterGap);
          }
        }
      }
      
      toast.success('Morse code playback completed');
    } catch (error) {
      console.error('Audio playback error:', error);
      toast.error('Failed to play Morse code audio');
    }
    
    setIsPlaying(false);
  };

  const stopPlayback = () => {
    setIsPlaying(false);
    // Note: In a real implementation, you would need to store and stop the audio context
    toast.success('Audio playback stopped');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Radio className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Morse Code Converter</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Convert text to Morse code and decode Morse code back to text. Includes audio playback feature for learning and practice.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Mode Selection */}
          <div className="flex justify-center mb-6">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setMode('encode')}
                className={`flex items-center gap-2 py-2 px-4 rounded-md transition-colors ${
                  mode === 'encode' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Text to Morse
              </button>
              <button
                onClick={() => setMode('decode')}
                className={`flex items-center gap-2 py-2 px-4 rounded-md transition-colors ${
                  mode === 'decode' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Morse to Text
              </button>
            </div>
          </div>

          {/* Input Section */}
          <div className="space-y-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {mode === 'encode' ? 'Enter text to convert to Morse code:' : 'Enter Morse code to convert to text:'}
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={
                  mode === 'encode' 
                    ? 'Type your message here...' 
                    : 'Enter Morse code (use dots, dashes, and spaces)...'
                }
                className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {mode === 'decode' && (
                <p className="text-xs text-gray-500 mt-1">
                  Use dots (.) and dashes (-) for Morse code. Separate letters with spaces and words with forward slashes (/).
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleConvert}
                disabled={!inputText.trim()}
                className="bg-purple-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mode === 'encode' ? 'Encode to Morse' : 'Decode to Text'}
              </button>
              
              <button
                onClick={swapMode}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                title="Swap input and output"
              >
                <ArrowRightLeft className="w-4 h-4" />
                Swap
              </button>
              
              <button
                onClick={reset}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>

          {/* Output Section */}
          {outputText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {mode === 'encode' ? 'Morse Code Output:' : 'Text Output:'}
                </h3>
                <div className="flex items-center gap-2">
                  {mode === 'encode' && (
                    <>
                      <button
                        onClick={() => setAudioEnabled(!audioEnabled)}
                        className={`p-2 rounded-lg transition-colors ${
                          audioEnabled ? 'text-purple-600 hover:bg-purple-100' : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={audioEnabled ? 'Disable audio' : 'Enable audio'}
                      >
                        {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                      </button>
                      
                      <button
                        onClick={isPlaying ? stopPlayback : playMorseCode}
                        disabled={!audioEnabled}
                        className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        {isPlaying ? 'Stop' : 'Play'}
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={() => copyToClipboard(outputText, mode === 'encode' ? 'Morse code' : 'text')}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
              </div>
              
              <div className={`bg-white rounded border p-4 ${mode === 'encode' ? 'font-mono text-lg letter-spacing-wide' : ''}`}>
                {outputText}
              </div>
              
              {mode === 'encode' && (
                <div className="mt-4 text-sm text-gray-600">
                  <p>Dots (·) = short signals, Dashes (−) = long signals</p>
                  <p>Spaces separate letters, forward slashes (/) separate words</p>
                </div>
              )}
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Morse Code Reference */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h4 className="font-semibold text-blue-900 mb-4">Morse Code Reference</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
              {Object.entries(morseCodeMap)
                .filter(([letter]) => letter.match(/[A-Z0-9]/))
                .map(([letter, morse]) => (
                  <div key={letter} className="flex justify-between bg-white rounded px-3 py-1">
                    <span className="font-semibold text-blue-900">{letter}</span>
                    <span className="font-mono text-blue-700">{morse}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">About Morse Code</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Developed by Samuel Morse in the 1830s</li>
                <li>• Uses dots and dashes to represent letters and numbers</li>
                <li>• Originally used for telegraph communication</li>
                <li>• Still used in amateur radio and aviation</li>
                <li>• International standard for emergency signals (SOS = ... --- ...)</li>
                <li>• Can be transmitted by sound, light, or radio waves</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Learning Tips</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• Start with common letters: E (.), T (-), A (.-), N (-.) </li>
                <li>• Practice with short words first</li>
                <li>• Use mnemonics: A = "A-bout", B = "Bee-hive-buzz-buzz"</li>
                <li>• Listen to audio to develop rhythm</li>
                <li>• Practice regularly for muscle memory</li>
                <li>• Learn emergency signals: SOS, MAYDAY</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MorseCodeConverter;
